import { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Chip,
  CircularProgress,
  Button,
} from '@mui/material';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';

import { useApi } from '@/providers/ApiContext';
import { getPlotMarker } from '@/actions/plotMarkers';
import { getPlotMarkerAsset } from '@/actions/plotMarkerAsset';
import DocumentViewDialog from '@/components/shared/DocumentViewDialog';
import PLOT_STATUSES from '@/components/plotStatuses';
import type { PlotMarker as PlotMarkerType, EntityId } from '@/types';

const POLL_INTERVAL = 5000; // 5 seconds

interface PlotMarkerProps {
  marker: PlotMarkerType;
  plotStatus?: string;
  onDelete: (markerId: EntityId) => void | Promise<void>;
  plotId: string | undefined;
  waypointId: EntityId;
}

function PlotMarker({
  marker,
  plotStatus,
  onDelete,
  plotId,
  waypointId,
}: PlotMarkerProps): React.JSX.Element {
  const api = useApi();
  const [currentMarker, setCurrentMarker] = useState<PlotMarkerType>(marker);
  const [loading, setLoading] = useState<boolean>(false);
  const [docDialogOpen, setDocDialogOpen] = useState<boolean>(false);
  const [docContent, setDocContent] = useState<string>('');
  const [docTitle, setDocTitle] = useState<string>('');
  const [docLoading, setDocLoading] = useState<boolean>(false);
  const [docPreloaded, setDocPreloaded] = useState<boolean>(false);

  const fetchMarker = useCallback(async () => {
    if (!plotId || !waypointId || !marker.entity_id || !api) return;

    try {
      setLoading(true);
      const data = await getPlotMarker(
        api,
        plotId as EntityId,
        waypointId,
        marker.entity_id,
      );
      setCurrentMarker(data);
    } catch (error) {
      console.error('Error fetching marker:', error);
    } finally {
      setLoading(false);
    }
  }, [api, plotId, waypointId, marker.entity_id]);

  useEffect(() => {
    let interval: NodeJS.Timeout | undefined;

    if (currentMarker?.status === 'processing') {
      interval = setInterval(fetchMarker, POLL_INTERVAL);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [currentMarker?.status, fetchMarker]);

  const fetchDocumentContent = useCallback(async () => {
    if (!plotId || !waypointId || !currentMarker?.entity_id || !api) return;

    try {
      setDocLoading(true);
      setDocContent('');
      setDocTitle('');

      // First get the manifest.json to understand the document structure
      const manifestData = await getPlotMarkerAsset(
        api,
        plotId as EntityId,
        waypointId,
        currentMarker.entity_id,
        'manifest.json',
      );

      if (!manifestData) {
        setDocContent('No document manifest available');
        setDocTitle(currentMarker.name);
        return;
      }

      const manifest = manifestData as unknown as {
        formatted?: string;
        name?: string;
        [key: string]: unknown;
      };

      // Get the formatted content based on manifest
      const contentData = await getPlotMarkerAsset(
        api,
        plotId as EntityId,
        waypointId,
        currentMarker.entity_id,
        manifest.formatted || 'content.txt',
      );

      setDocContent(
        typeof contentData === 'string' ? contentData : 'No content available',
      );
      setDocTitle(manifest.name || currentMarker.name);
    } catch (error) {
      console.error('Error fetching document content:', error);
      setDocContent('Error loading document content');
      setDocTitle(currentMarker.name);
    } finally {
      setDocLoading(false);
    }
  }, [api, plotId, waypointId, currentMarker?.entity_id, currentMarker?.name]);

  // Preload document content when marker is ready and not already preloaded
  useEffect(() => {
    if (
      currentMarker?.status === 'ready' &&
      !docPreloaded &&
      !docContent &&
      !docLoading
    ) {
      fetchDocumentContent();
      setDocPreloaded(true);
    }
  }, [
    currentMarker?.status,
    docPreloaded,
    docContent,
    docLoading,
    fetchDocumentContent,
  ]);

  const handleViewDocument = () => {
    setDocDialogOpen(true);
    // If content is already preloaded, don't fetch again
    if (!docContent) {
      fetchDocumentContent();
    }
  };

  const getStatusColor = (
    status?: string,
  ): 'success' | 'warning' | 'error' | 'default' => {
    switch (status) {
      case 'ready':
        return 'success';
      case 'processing':
        return 'warning';
      case 'errored':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status?: string): string => {
    switch (status) {
      case 'ready':
        return 'Ready';
      case 'processing':
        return 'Processing';
      case 'errored':
        return 'Error';
      default:
        return status || 'Unknown';
    }
  };
  return (
    <>
      <Box
        sx={{
          p: 1,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: 'background.paper',
        }}
      >
        <Box
          sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 1 }}
        >
          {loading && <CircularProgress size={12} />}
          <Typography variant='body2' sx={{ fontSize: '0.875rem' }}>
            {currentMarker.name}
          </Typography>
          <Chip
            label={getStatusLabel(currentMarker.status)}
            color={getStatusColor(currentMarker.status)}
            size='small'
            variant='outlined'
            sx={{ height: 20, fontSize: '0.7rem' }}
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {currentMarker.status === 'ready' && (
            <Button
              size='small'
              onClick={handleViewDocument}
              disabled={docLoading}
              startIcon={<VisibilityOutlinedIcon />}
              sx={{
                minWidth: 'auto',
                px: 1,
                py: 0.5,
                fontSize: '0.75rem',
                textTransform: 'none',
              }}
            >
              {docLoading
                ? 'Loading...'
                : docContent
                  ? 'View'
                  : 'Preloading...'}
            </Button>
          )}
          {plotStatus !== PLOT_STATUSES.COMPLETE && (
            <IconButton
              size='small'
              onClick={() => onDelete(currentMarker.entity_id)}
              color='error'
              sx={{ p: 0.5 }}
            >
              <DeleteOutlinedIcon sx={{ fontSize: '1rem' }} />
            </IconButton>
          )}
        </Box>
      </Box>

      <DocumentViewDialog
        title={docTitle}
        contents={docContent}
        open={docDialogOpen}
        handleClose={() => setDocDialogOpen(false)}
      />
    </>
  );
}

export default PlotMarker;

